import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
  Box,
  Icon,
  useBreakpointValue,
  Link,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { FaUniversity, FaShieldAlt, FaExternalLinkAlt } from 'react-icons/fa';
import { useWalletContext } from '../../context/WalletContext';
import axiosInstance from '../../services/axios';

const PayoutModal = ({ isOpen, onClose, onSuccess, availableBalance = 0 }) => {
  const { handleSubmit, formState: { errors }, control, watch, setValue } = useForm();
  const { payoutToBank } = useWalletContext();
  
  const [loading, setLoading] = useState(false);
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [checkingStripe, setCheckingStripe] = useState(true);
  
  // Responsive values
  const modalSize = useBreakpointValue({ base: 'full', md: 'xl' });
  
  // Form values
  const payoutAmountRaw = watch('amount', 0);
  const payoutAmount = parseFloat(payoutAmountRaw) || 0;
  
  // Quick amount options (percentages of available balance)
  const quickAmounts = [
    { label: '25%', value: Math.floor(availableBalance * 0.25) },
    { label: '50%', value: Math.floor(availableBalance * 0.5) },
    { label: '75%', value: Math.floor(availableBalance * 0.75) },
    { label: 'All', value: Math.floor(availableBalance) }
  ].filter(option => option.value >= 10); // Only show options >= minimum

  const handlePayout = async (data) => {
    setLoading(true);
    try {
      await payoutToBank({
        amount: parseFloat(data.amount)
      });
      onSuccess();
    } catch (error) {
      // Error is already handled in the hook with toast
      console.error('Payout error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAmount = (amount) => {
    setValue('amount', amount);
  };

  // Check Stripe account status when modal opens
  useEffect(() => {
    if (isOpen) {
      checkStripeAccountStatus();
    } else {
      // Reset state when modal closes
      setStripeAccountStatus(null);
      setCheckingStripe(true);
    }
  }, [isOpen]);

  const checkStripeAccountStatus = async () => {
    try {
      setCheckingStripe(true);
      const response = await axiosInstance.get('/stripe/connect/account');
      setStripeAccountStatus({
        connected: true,
        details_submitted: response.data.details_submitted,
        payouts_enabled: response.data.payouts_enabled,
        requirements: response.data.requirements
      });
    } catch (error) {
      console.error('Error checking Stripe account status:', error);
      setStripeAccountStatus({
        connected: false,
        error: error.response?.status === 404 ? 'not_found' : 'error'
      });
    } finally {
      setCheckingStripe(false);
    }
  };

  const setupStripeAccount = async () => {
    try {
      setLoading(true);
      
      // First create the account
      await axiosInstance.post('/stripe/connect/create-account');
      
      // Then get onboarding link
      const response = await axiosInstance.post('/stripe/connect/generate-onboarding-link', {
        return_url: `${window.location.origin}/dashboard/wallet`,
        refresh_url: `${window.location.origin}/dashboard/wallet`
      });
      
      console.log('Onboarding URL received:', response.data.onboarding_url);
      
      // Redirect to Stripe onboarding - use window.location instead of window.open
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      } else {
        console.error('No onboarding URL in response:', response.data);
      }
    } catch (error) {
      console.error('Error setting up Stripe account:', error);
      // Could add toast notification here for errors
    } finally {
      setLoading(false);
    }
  };

  const canPayout = availableBalance >= 10 && stripeAccountStatus?.payouts_enabled;
  const processingTime = "1-2 business days";

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={modalSize} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader textAlign="center" fontWeight="bold">
          Withdraw to Bank Account
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <form onSubmit={handleSubmit(handlePayout)}>
            <VStack spacing={6}>
              {/* Available Balance Display */}
              <Box w="100%" p={4} borderRadius="md">
                <VStack spacing={2}>
                  <Text fontSize="sm" textTransform="uppercase">
                    Available Balance
                  </Text>
                  <Text fontSize="2xl" fontWeight="bold">
                    ${availableBalance.toFixed(2)}
                  </Text>
                  <Text fontSize="xs">
                    Minimum withdrawal: $10.00
                  </Text>
                </VStack>
              </Box>

              {checkingStripe ? (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">Checking Stripe account status...</Text>
                </Alert>
              ) : !stripeAccountStatus?.connected ? (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={3} w="100%">
                    <Text fontSize="sm" fontWeight="bold">
                      Stripe Account Required
                    </Text>
                    <Text fontSize="xs">
                      To withdraw funds, you need to connect a Stripe account for secure bank transfers.
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        variant="brand"
                        onClick={setupStripeAccount}
                        isLoading={loading}
                        loadingText="Redirecting..."
                        rightIcon={!loading && <Icon as={FaExternalLinkAlt} />}
                      >
                        Connect Stripe Account
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={checkStripeAccountStatus}
                        isLoading={checkingStripe}
                      >
                        Refresh Status
                      </Button>
                    </HStack>
                  </VStack>
                </Alert>
              ) : !stripeAccountStatus?.payouts_enabled ? (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={3} w="100%">
                    <Text fontSize="sm" fontWeight="bold">
                      Stripe Account Setup Incomplete
                    </Text>
                    <Text fontSize="xs">
                      Your Stripe account needs additional information before you can receive payouts.
                      {stripeAccountStatus?.requirements?.currently_due?.length > 0 && 
                        ` Required: ${stripeAccountStatus.requirements.currently_due.join(', ')}`
                      }
                    </Text>
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        variant="brand"
                        onClick={setupStripeAccount}
                        isLoading={loading}
                        loadingText="Redirecting..."
                        rightIcon={!loading && <Icon as={FaExternalLinkAlt} />}
                      >
                        Complete Stripe Setup
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={checkStripeAccountStatus}
                        isLoading={checkingStripe}
                      >
                        Refresh Status
                      </Button>
                    </HStack>
                  </VStack>
                </Alert>
              ) : availableBalance < 10 ? (
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="bold">
                      Insufficient funds for withdrawal
                    </Text>
                    <Text fontSize="xs">
                      You need at least $10.00 in available balance to withdraw to your bank account.
                    </Text>
                  </VStack>
                </Alert>
              ) : (
                <>
                  {/* Amount Selection */}
                  <FormControl isInvalid={errors.amount}>
                    <FormLabel>Withdrawal Amount</FormLabel>
                    <VStack spacing={3}>
                      <Controller
                        name="amount"
                        control={control}
                        rules={{
                          required: "Amount is required",
                          min: { value: 10, message: "Minimum withdrawal is $10.00" },
                          max: { value: availableBalance, message: `Maximum withdrawal is $${availableBalance.toFixed(2)}` }
                        }}
                        render={({ field }) => (
                          <NumberInput
                            {...field}
                            min={10}
                            max={availableBalance}
                            precision={2}
                            w="100%"
                          >
                            <NumberInputField
                              placeholder="$10.00"
                              variant="light"
                            />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        )}
                      />
                      
                      {/* Quick Amount Buttons */}
                      {quickAmounts.length > 0 && (
                        <HStack spacing={2} w="100%" justify="center">
                          {quickAmounts.map((option) => (
                            <Button
                              key={option.label}
                              size="sm"
                              variant={payoutAmount === option.value ? "brand" : "outline"}
                              onClick={() => handleQuickAmount(option.value)}
                              minW="60px"
                            >
                              {option.label}
                            </Button>
                          ))}
                        </HStack>
                      )}
                    </VStack>
                    <FormErrorMessage>{errors.amount && errors.amount.message}</FormErrorMessage>
                  </FormControl>

                  {/* Processing Info */}
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="bold">
                        Processing Time: {processingTime}
                      </Text>
                      <Text fontSize="xs">
                        Funds will be transferred to your connected bank account via Stripe.
                        No fees are charged for standard bank transfers.
                      </Text>
                    </VStack>
                  </Alert>

                  {/* Bank Account Info */}
                  <Box w="100%" p={4} borderRadius="md">
                    <VStack spacing={2}>
                      <HStack>
                        <Icon as={FaUniversity} />
                        <Text fontSize="sm" fontWeight="bold">
                          Connected Bank Account
                        </Text>
                      </HStack>
                      <Text fontSize="xs" textAlign="center">
                        Funds will be sent to your default bank account on file.
                        To change your bank account, visit your account settings.
                      </Text>
                    </VStack>
                  </Box>

                  {/* Security Notice */}
                  <Box w="100%" p={3} borderRadius="md">
                    <HStack>
                      <Icon as={FaShieldAlt} />
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="bold">
                          Secure Transfer
                        </Text>
                        <Text fontSize="xs">
                          All withdrawals are processed securely through Stripe with bank-level encryption.
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                </>
              )}
            </VStack>
          </form>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3} w="100%">
            <Button variant="outline" onClick={onClose} flex={1}>
              Cancel
            </Button>
            {canPayout && (
              <Button
                variant="brand"
                onClick={handleSubmit(handlePayout)}
                isLoading={loading}
                loadingText="Processing..."
                flex={2}
                isDisabled={!payoutAmount || payoutAmount < 10 || payoutAmount > availableBalance}
              >
                Withdraw ${(payoutAmount || 0).toFixed(2)}
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PayoutModal;